import { useState, useCallback } from 'react';

export const useSpreadsheetSelection = () => {
  const [selectionStart, setSelectionStart] = useState(null);
  const [selectionEnd, setSelectionEnd] = useState(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const [activeCell, setActiveCell] = useState(null);
  const [editingCell, setEditingCell] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [copiedCells, setCopiedCells] = useState(null);
  const [copiedCellsRange, setCopiedCellsRange] = useState(null);
  const [isSelectingRows, setIsSelectingRows] = useState(false);

  const selectEntireRow = useCallback((rowIndex, visibleColumnNames) => {
    setSelectionStart([rowIndex, 0]);
    setSelectionEnd([rowIndex, Object.keys(visibleColumnNames).length - 1]);
    setActiveCell([rowIndex, 0]);
  }, []);

  const selectAllCells = useCallback((filteredData, visibleColumnNames) => {
    if (filteredData.length === 0) return;
    setSelectionStart([0, 0]);
    setSelectionEnd([
      filteredData.length - 1,
      Object.keys(visibleColumnNames).length - 1,
    ]);
    setActiveCell([0, 0]);
  }, []);

  const isCellSelected = useCallback(
    (row, col) => {
      if (copiedCellsRange) {
        const { minRow, maxRow, minCol, maxCol } = copiedCellsRange;
        return (
          row >= minRow && row <= maxRow && col >= minCol && col <= maxCol
        );
      }
      if (!selectionStart || !selectionEnd) return false;
      const [startRow, startCol] = selectionStart;
      const [endRow, endCol] = selectionEnd;
      const minRow = Math.min(startRow, endRow);
      const maxRow = Math.max(startRow, endRow);
      const minCol = Math.min(startCol, endCol);
      const maxCol = Math.max(startCol, endCol);
      return (
        row >= minRow && row <= maxRow && col >= minCol && col <= maxCol
      );
    },
    [copiedCellsRange, selectionStart, selectionEnd]
  );

  const getCellBorderStyle = useCallback(
    (rowIndex, colIndex) => {
      if (!copiedCellsRange) return {};
      const { minRow, maxRow, minCol, maxCol } = copiedCellsRange;
      const borderStyle = '2px dashed #888';
      const style = {};

      if (
        rowIndex >= minRow &&
        rowIndex <= maxRow &&
        colIndex >= minCol &&
        colIndex <= maxCol
      ) {
        if (rowIndex === minRow) style.borderTop = borderStyle;
        if (rowIndex === maxRow) style.borderBottom = borderStyle;
        if (colIndex === minCol) style.borderLeft = borderStyle;
        if (colIndex === maxCol) style.borderRight = borderStyle;
      }

      return style;
    },
    [copiedCellsRange]
  );

  return {
    selectionStart,
    setSelectionStart,
    selectionEnd,
    setSelectionEnd,
    isSelecting,
    setIsSelecting,
    activeCell,
    setActiveCell,
    editingCell,
    setEditingCell,
    editingValue,
    setEditingValue,
    copiedCells,
    setCopiedCells,
    copiedCellsRange,
    setCopiedCellsRange,
    isSelectingRows,
    setIsSelectingRows,
    selectEntireRow,
    selectAllCells,
    isCellSelected,
    getCellBorderStyle
  };
};